import { useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import styled, { keyframes } from "styled-components";
import Reveal from "../v2/Reveal";
import DivisionCinema from "./DivisionCinema";
import ParticleSculpture from "./ParticleSculpture";
import playWarpTransition from "../v2/warpTransition";
import {
	SectionV2,
	InnerV2,
	HeadV2,
	PillButton,
	accents,
} from "../v2/primitives";

const ROTATION_MS = 14000;

const JOURNEYS = [
	{
		key: "acceleration",
		accent: accents.green,
		mode: "accelerator",
		// the hero's idea bulb, echoed here as the spark being accelerated
		sculpture: "bulb",
	},
	{
		key: "disruption",
		accent: accents.cyan,
		mode: "liftoff",
		// particle rocket riding the stage's thruster exhaust
		sculpture: "rocket",
	},
	{
		key: "expansion",
		accent: accents.violet,
		mode: "expansion",
		// particle globe with graticule and glowing city hubs; to revert to
		// the original artwork, just delete the `sculpture` line below
		sculpture: "globe",
		image: "/landings/assets/images/business_division/expansion.svg",
		imagePos: "50% 50%",
	},
];

export const BusinessDivision = () => {
	const { t } = useTranslation();
	const router = useRouter();
	const [active, setActive] = useState(0);
	const [paused, setPaused] = useState(false);
	const [inView, setInView] = useState(false);
	const sectionRef = useRef(null);
	const cinemaRef = useRef(null);
	const btnRefs = useRef([]);
	const discRef = useRef(null);
	const tiltRef = useRef(null);
	const remainingRef = useRef(ROTATION_MS);
	const startedAtRef = useRef(0);

	const journey = JOURNEYS[active];

	// the carousel only plays while the section is on screen, so it always
	// opens on the first unit when the visitor scrolls to it (and it holds its
	// place, in sync, when scrolled away)
	const frozen = paused || !inView;

	// each unit gets a fresh clock when it takes the stage (must run before
	// the timer effect below so its cleanup bookkeeping is overwritten)
	useEffect(() => {
		remainingRef.current = ROTATION_MS;
	}, [active]);

	// units take the stage in turns; hovering a selector item (or scrolling the
	// section off screen) holds the current one, and the clock resumes from
	// where it stopped
	useEffect(() => {
		if (frozen) return undefined;
		startedAtRef.current = Date.now();
		const id = setTimeout(
			() => setActive((current) => (current + 1) % JOURNEYS.length),
			remainingRef.current,
		);
		return () => {
			clearTimeout(id);
			remainingRef.current = Math.max(
				1000,
				remainingRef.current - (Date.now() - startedAtRef.current),
			);
		};
	}, [active, frozen]);

	// gate the rotation on visibility so it starts fresh on the first unit
	useEffect(() => {
		const el = sectionRef.current;
		if (!el) return undefined;
		if (typeof IntersectionObserver === "undefined") {
			setInView(true);
			return undefined;
		}
		const io = new IntersectionObserver(
			([entry]) => setInView(entry.isIntersecting),
			{ threshold: 0.3 },
		);
		io.observe(el);
		return () => io.disconnect();
	}, []);

	// touch has no hover: a tap would freeze the rotation with no way out
	const holdRotation = (on) => {
		if (window.matchMedia("(hover: hover)").matches) setPaused(on);
	};

	// choreography: the energy wave is born at the unit's selector button and
	// sweeps the whole section; the disc anchor is re-measured after layout
	useEffect(() => {
		const api = cinemaRef.current;
		const btn = btnRefs.current[active];
		if (!api || !btn) return;
		const canvasRect = api.getRect();
		if (!canvasRect) return;
		const j = JOURNEYS[active];
		const b = btn.getBoundingClientRect();
		api.transition({
			mode: j.mode,
			accent: j.accent,
			x: b.left + b.width / 2 - canvasRect.left,
			y: b.top + b.height / 2 - canvasRect.top,
		});
		const measureAnchor = () => {
			const disc = discRef.current;
			const rect = api.getRect();
			if (!disc || !rect) return;
			const d = disc.getBoundingClientRect();
			// center survives the scale-in transform (center origin), but the
			// radius must come from layout size or it reads ~18% small
			api.setAnchor(
				d.left + d.width / 2 - rect.left,
				d.top + d.height / 2 - rect.top,
				disc.offsetWidth / 2,
			);
		};
		requestAnimationFrame(measureAnchor);
		window.addEventListener("resize", measureAnchor);
		window.addEventListener("scroll", measureAnchor, { passive: true });
		return () => {
			window.removeEventListener("resize", measureAnchor);
			window.removeEventListener("scroll", measureAnchor);
		};
	}, [active]);

	// 3D tilt following the pointer (rAF-throttled)
	useEffect(() => {
		const el = tiltRef.current;
		if (!el) return undefined;
		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reducedMotion) return undefined;

		let rafId = null;
		let targetX = 0;
		let targetY = 0;
		let curX = 0;
		let curY = 0;

		const apply = () => {
			curX += (targetX - curX) * 0.08;
			curY += (targetY - curY) * 0.08;
			el.style.transform = `perspective(900px) rotateX(${curY.toFixed(2)}deg) rotateY(${curX.toFixed(2)}deg)`;
			rafId =
				Math.abs(curX - targetX) + Math.abs(curY - targetY) > 0.01
					? requestAnimationFrame(apply)
					: null;
		};
		const schedule = () => {
			if (rafId === null) rafId = requestAnimationFrame(apply);
		};
		const onMove = (e) => {
			const rect = el.getBoundingClientRect();
			targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
			targetY = -((e.clientY - rect.top) / rect.height - 0.5) * 8;
			schedule();
		};
		const onLeave = () => {
			targetX = 0;
			targetY = 0;
			schedule();
		};

		el.addEventListener("pointermove", onMove, { passive: true });
		el.addEventListener("pointerleave", onLeave);
		return () => {
			if (rafId) cancelAnimationFrame(rafId);
			el.removeEventListener("pointermove", onMove);
			el.removeEventListener("pointerleave", onLeave);
		};
	}, []);

	// real hrefs so crawlers can follow the division links; the click is
	// intercepted so the division leaves through the cinematic warp
	const divisionHref = (key) =>
		`/${router.query.locale}/business-division/${key}/`;

	const openDivision = (key, event) => {
		event?.preventDefault();
		const rect = event?.currentTarget?.getBoundingClientRect?.();
		playWarpTransition({
			x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
			y: rect ? rect.top + rect.height / 2 : window.innerHeight / 2,
			accent: JOURNEYS.find((j) => j.key === key)?.accent || accents.green,
			navigate: () => router.push(divisionHref(key)),
		});
	};

	const title = t("businessDivision.title");
	const titleWords = title.split(" ");
	const lastWord = titleWords.pop();

	return (
		<SectionV2
			ref={sectionRef}
			id="who-we-are"
			$accent={journey.accent}
			$glowAt="50% 0%"
		>
			<DivisionCinema
				ref={cinemaRef}
				initialMode={JOURNEYS[0].mode}
				initialAccent={JOURNEYS[0].accent}
			/>
			<InnerV2>
				<Reveal>
					<HeadV2 $accent={journey.accent}>
						<span className="overline">
							{t("businessDivision.businessDivision")}
						</span>
						<h2>
							{titleWords.join(" ")} <em>{lastWord}</em>
						</h2>
						<p>{t("businessDivision.text")}</p>
					</HeadV2>
				</Reveal>

				<Reveal y={34}>
					<StageGrid>
						<Selector
							role="tablist"
							aria-orientation="vertical"
							aria-label={t("businessDivision.title")}
						>
							{JOURNEYS.map((j, i) => (
								<SelectorItem
									key={j.key}
									ref={(el) => {
										btnRefs.current[i] = el;
									}}
									role="tab"
									type="button"
									aria-selected={i === active}
									$accent={j.accent}
									$active={i === active}
									$paused={frozen}
									onClick={() => setActive(i)}
									onMouseEnter={() => holdRotation(true)}
									onMouseLeave={() => holdRotation(false)}
								>
									<span className="track" aria-hidden="true">
										<i
											key={i === active ? `run-${active}` : `idle-${i}`}
											className={i === active ? "fill on" : "fill"}
										/>
									</span>
									<span className="labels">
										<span className="num">
											{String(i + 1).padStart(2, "0")}
										</span>
										<span className="name">
											{t(`businessDivision.${j.key}`)}
										</span>
									</span>
								</SelectorItem>
							))}
						</Selector>

						<Stage ref={tiltRef}>
							<StageInner key={journey.key} $accent={journey.accent}>
								<div className={`disc ${journey.mode}`} ref={discRef}>
									<span className="halo" aria-hidden="true" />
									{journey.sculpture ? (
										<ParticleSculpture
											shape={journey.sculpture}
											accent={journey.accent}
										/>
									) : (
										<span className="frame">
											<img
												src={journey.image}
												alt=""
												loading="lazy"
												style={{ objectPosition: journey.imagePos }}
											/>
										</span>
									)}
								</div>
							</StageInner>
						</Stage>

						<Story key={`story-${journey.key}`} $accent={journey.accent}>
							<span className="hook">
								{t(`businessDivision.${journey.key}Hook`)}
							</span>
							<h3>{t(`businessDivision.${journey.key}`)}</h3>
							{/* the CMS copy splits this line in two parts (Title + Title2) */}
							<p className="stage-line">
								{`${t(`businessDivision.${journey.key}Title`)} ${t(
									`businessDivision.${journey.key}Title2`,
								)}`.trim()}
							</p>
							<p className="body">
								{t(`businessDivision.${journey.key}Text`)}
							</p>
							<PillButton
								as="a"
								href={divisionHref(journey.key)}
								$accent={journey.accent}
								onClick={(event) => openDivision(journey.key, event)}
							>
								{t("businessDivision.knowMore")}
								<svg
									width="15"
									height="15"
									viewBox="0 0 16 16"
									fill="none"
									aria-hidden="true"
								>
									<path
										d="M3 8h10M9 4l4 4-4 4"
										stroke="currentColor"
										strokeWidth="1.6"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</PillButton>
						</Story>
					</StageGrid>
				</Reveal>
			</InnerV2>
		</SectionV2>
	);
};

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(22px);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const discIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.82);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
`;

const discFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const fillRun = keyframes`
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
`;

const StageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: center;

  @media (min-width: 992px) {
    grid-template-columns: 200px 1.05fr 1fr;
    gap: 40px;
  }
`;

const Selector = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 18px;

  @media (min-width: 992px) {
    flex-direction: column;
    gap: 26px;
  }
`;

const SelectorItem = styled.button`
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 4px 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;

  /* timeline track: fills for the active unit over its screen time */
  .track {
    width: 2px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.12);
    overflow: hidden;

    .fill {
      display: block;
      width: 100%;
      height: 100%;
      background: ${(p) => p.$accent};
      transform: scaleY(0);
      transform-origin: top;

      &.on {
        animation: ${fillRun} ${ROTATION_MS}ms linear forwards;
        /* freezes with the rotation clock while a selector item is hovered */
        animation-play-state: ${(p) => (p.$paused ? "paused" : "running")};
        box-shadow: 0 0 12px ${(p) => p.$accent + "aa"};
      }
    }
  }

  .labels {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .num {
    font-family: "Hind Madurai";
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2px;
    color: ${(p) => (p.$active ? p.$accent : "#5c6470")};
    transition: color 0.3s ease;
  }

  .name {
    font-family: "Hind Madurai";
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: ${(p) => (p.$active ? "#ffffff" : "#8b93a1")};
    text-shadow: ${(p) => (p.$active ? `0 0 22px ${p.$accent}8c` : "none")};
    transition: color 0.3s ease, text-shadow 0.3s ease;
  }

  &:hover .name {
    color: #ffffff;
  }

  @media (max-width: 991px) {
    .name {
      font-size: 12.5px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .track .fill.on {
      animation: none;
      transform: scaleY(1);
    }
  }
`;

const Stage = styled.div`
  will-change: transform;
  transform-style: preserve-3d;
`;

const StageInner = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 10 / 8.5;
  max-width: 560px;
  margin: 0 auto;

  .disc {
    position: absolute;
    left: 50%;
    top: 50%;
    /* sculpture sized to fill the free vertical space of the stage; margin is
       half the width (centering) and follows it */
    width: 62%;
    aspect-ratio: 1;
    margin: -31% 0 0 -31%;
    /* enters in the wake of the energy wave */
    animation: ${discIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.28s both;

    /* soft halo drawn by us — never clipped into a square like the glow
       baked into the source artwork */
    .halo {
      position: absolute;
      inset: -22%;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        ${(p) => p.$accent + "3d"} 0%,
        ${(p) => p.$accent + "14"} 46%,
        transparent 70%
      );
      filter: blur(4px);
    }

    /* circular window over the artwork */
    .frame {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      overflow: hidden;
      border: 1px solid ${(p) => p.$accent + "4d"};
      box-shadow:
        inset 0 0 52px rgba(0, 1, 4, 0.6),
        0 18px 48px -18px ${(p) => p.$accent + "59"};
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &.liftoff {
      animation:
        ${discIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.28s both,
        ${discFloat} 3.2s ease-in-out 1s infinite;
    }

    &.accelerator {
      animation:
        ${discIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.28s both,
        ${discFloat} 5.5s ease-in-out 1s infinite;
    }

    &.expansion {
      animation:
        ${discIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.28s both,
        ${discFloat} 4.4s ease-in-out 1s infinite;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .disc,
    .disc img {
      animation: none;
    }
  }
`;

const Story = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  animation: ${rise} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.34s both;

  .hook {
    font-family: "Hind Madurai";
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: ${(p) => p.$accent};
    margin-bottom: 10px;
  }

  /* same treatment as the accented word in the section title */
  h3 {
    font-family: "Hind Madurai";
    font-size: clamp(1.7rem, 3vw, 2.5rem);
    font-weight: 600;
    letter-spacing: -0.4px;
    text-transform: capitalize;
    color: ${(p) => p.$accent};
    text-shadow: 0 0 30px ${(p) => p.$accent + "59"};
    margin: 0 0 12px 0;
  }

  .stage-line {
    font-family: "Hind Madurai";
    font-size: 1.02rem;
    font-weight: 600;
    line-height: 1.55;
    color: #d7dbe2;
    margin: 0 0 10px 0;
  }

  .body {
    font-family: "Hind Madurai";
    font-size: 0.98rem;
    line-height: 1.7;
    color: #a9afb8;
    margin: 0 0 26px 0;
  }

  @media (max-width: 991px) {
    align-items: center;
    text-align: center;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
